import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const translations = {
  en: {
    nav: {
      home: "Home",
      services: "Services",
      enclave: "Enclave KMS",
      blog: "Blog",
      team: "Team",
      contact: "Contact",
    },
    misc: {
      skip: "Skip to content",
      languageLabel: "Language",
      languageEnglish: "English",
      languageFrench: "French",
      themeDark: "Dark",
      themeLight: "Light",
      switchToDarkTheme: "Switch to dark theme",
      switchToLightTheme: "Switch to light theme",
    },
    banner: {
      eyebrow: "Custom Software Solutions",
      headingPrefix: "We are",
      headingSuffix: "building software that helps teams",
      headingEmphasis: "move with clarity.",
      subtext:
        "We design and ship dependable software for organizations that need clean systems, practical product thinking, and delivery they can trust.",
      cta: "Start a project",
    },
    homeUpdate: {
      eyebrow: "Product update",
      productName: "Quivio Enclave KMS",
      productLabel: "Governed AI knowledge base",
      heading: "Quivio Enclave KMS is now available.",
      body:
        "A governed on-premises knowledge intelligence platform for teams that need private AI, role-scoped answers, citations, and audit trails.",
      primaryCta: "Explore Enclave",
      secondaryCta: "Read the announcement",
      panelTitle: "Deployment profile",
      panelStatus: "On-prem",
      receiptLabel: "Audit receipt",
      points: ["On-premises deployment", "RBAC retrieval", "Approval and audit trails"],
    },
    about: {
      heading: "Software design and engineering for complex operations",
      bodyLead: "We help teams clarify the problem, shape the product, and build the system behind it,",
      bodyTail:
        "then keep improving the experience, architecture, and business outcomes after launch.",
    },
    aiResearch: {
      eyebrow: "Applied AI research",
      heading: "Applied AI research for organizations that need more than demos.",
      body:
        "We study the workflow, the people, the data, and the constraints before recommending AI. That gives your team a realistic path to value instead of a pile of disconnected experiments.",
      primaryCta: "See AI services",
      secondaryCta: "Talk about AI readiness",
      signalsEyebrow: "What we validate",
      signalsHeading: "Readiness signals",
      signals: ["Workflow bottlenecks", "Knowledge access patterns", "Risk and approval boundaries", "Operational ownership"],
      tracks: [
        {
          eyebrow: "Workflow research",
          title: "Find where AI can remove friction",
          description:
            "We map decisions, handoffs, and failure points to isolate the AI opportunities that are worth your team's time and budget.",
        },
        {
          eyebrow: "Prototype validation",
          title: "Test the system before scaling the story",
          description:
            "We turn the strongest use cases into constrained pilots so your organization can measure usefulness, quality, and adoption before committing to a larger rollout.",
        },
        {
          eyebrow: "Governance design",
          title: "Define the controls that make adoption defensible",
          description:
            "We shape the operating model around data access, human review, auditability, and ownership so AI can be introduced without bypassing accountability.",
        },
      ],
    },
    why: {
      heading: "How we make software easier to trust",
      intro:
        "Good software is not just shipped code. It is shared language, sound architecture, steady delivery, and clear evidence that the system is doing the right thing.",
      items: [
        {
          id: "clientFocused",
          title: "Close to your team",
          description:
            "We work with clear checkpoints, fast feedback loops, and direct updates so the product keeps reflecting your real goals.",
        },
        {
          id: "expertise",
          title: "Senior engineering judgment",
          description:
            "We turn ambiguous requirements into maintainable systems, with decisions grounded in product needs and technical reality.",
        },
        {
          id: "efficientProcess",
          title: "Predictable delivery",
          description:
            "Focused sprints, validated decisions, and visible tradeoffs keep momentum high without hiding complexity.",
        },
        {
          id: "qualityInnovation",
          title: "Quality that lasts",
          description:
            "We use modern tools pragmatically, prioritizing reliability, performance, and systems your team can operate over time.",
        },
      ],
    },
    aiFunnel: {
      eyebrow: "AI readiness funnel",
      heading: "A practical path from interest to operational AI.",
      body:
        "We help organizations move from scattered experiments to an AI-ready operating model by validating value in small loops, then hardening the process, data, and controls behind it.",
      stagesHeading: "AI readiness stages",
      stageLabel: "Stage",
      resultLabel: "Result",
      stages: [
        {
          step: "01",
          eyebrow: "Align",
          title: "Frame the business problem clearly",
          description:
            "We identify the objective, the workflow pressure points, the stakeholders, and the risks so everyone is solving the same problem.",
          result: "Shared success criteria",
        },
        {
          step: "02",
          eyebrow: "Validate",
          title: "Design a pilot with evidence behind it",
          description:
            "We define the inputs, review loops, and evaluation signals needed to prove whether AI will help in a controlled environment.",
          result: "Credible pilot scope",
        },
        {
          step: "03",
          eyebrow: "Operationalize",
          title: "Build the workflow around the model",
          description:
            "We connect the system to the right knowledge, interfaces, and human checkpoints so the solution can fit real operations instead of living in a sandbox.",
          result: "Production-shaped workflow",
        },
        {
          step: "04",
          eyebrow: "Govern",
          title: "Make AI adoption sustainable",
          description:
            "We establish ownership, controls, and monitoring so your organization can expand usage with clarity about quality, risk, and accountability.",
          result: "AI-ready operating model",
        },
      ],
      outcomeEyebrow: "Where this leads",
      outcomeHeading: "What being AI ready looks like",
      outcomes: [
        "A prioritized list of AI use cases tied to business outcomes",
        "Pilot designs with review loops and measurable success criteria",
        "Knowledge, data, and tooling requirements made explicit",
        "Governance patterns your team can operate after launch",
      ],
    },
    services: {
      heading: "Services",
      subheading: "Quivio services are built for your business needs",
      items: [
        {
          id: "enterprise",
          title: "Enterprise Application Development",
          description:
            "Tailored solutions to streamline your business operations. We create robust, scalable applications that integrate seamlessly with your existing systems and grow with your enterprise.",
        },
        {
          id: "uiux",
          title: "UI/UX Design",
          description:
            "Crafting user-centric interfaces that elevate your digital presence. Our designs enhance user engagement, improve productivity, and create memorable experiences.",
        },
        {
          id: "cloud",
          title: "Cloud Solutions",
          description:
            "Secure, flexible cloud infrastructure tailored to your needs. We facilitate smooth cloud migration, ensuring scalability, cost-efficiency, and enhanced collaboration for your business.",
        },
        {
          id: "mobile",
          title: "Mobile App Development",
          description:
            "Cross-platform mobile applications that captivate users. We build responsive, feature-rich apps that provide seamless experiences across devices and operating systems.",
        },
        {
          id: "appliedAi",
          title: "Applied AI Research",
          description:
            "We help organizations identify where AI belongs, validate the workflow with real evidence, and define the systems and governance needed to adopt it responsibly.",
        },
      ],
    },
    contact: {
      heading: "Contact",
      intro:
        "Say hello. Tell us what you're building and we'll reply with clear next steps, budgets, and timelines. Expect straight answers, modern thinking, and delivery you can trust.",
      nameLabel: "Name",
      namePlaceholder: "Name",
      subjectLabel: "Subject",
      subjectPlaceholder: "Subject",
      emailLabel: "Email",
      emailPlaceholder: "Email",
      messageLabel: "Message",
      messagePlaceholder: "Message",
      submit: "Send Message",
    },
    team: {
      heading: "Our team",
      subheading: "Great work, great people.",
      positions: {
        founder: "Founder / Chief Software Engineer",
        designDirector: "Design Director",
        softwareEngineer: "Software Engineer",
      },
    },
    lead: {
      heading: "Bring us the hard part",
      subheading: "We will help you turn it into a product, platform, or workflow your team can depend on.",
      cta: "Start the conversation",
    },
    portfolio: {
      heading: "Take a look at our portfolio",
      intro:
        "For every complex problem, there is an answer that is clear, simple, and wrong. We guide you with solutions that embrace the complexity and deliver clarity.",
      viewProject: "View project",
    },
    blog: {
      heading: "Blog",
      subheading: "Insights, updates, and case studies from the Quivio team.",
      readMore: "Read more",
      backToBlog: "Back to blog",
      notFound: "Post not found",
      loading: "Loading post...",
      loadError: "Unable to load this post right now.",
      readAloud: "Read aloud",
      stopReading: "Stop reading",
      readAloudUnavailable: "Read-aloud not supported on this device.",
      readingProgress: "Reading progress",
    },
    enclave: {
      productAlt: "Quivio Enclave KMS",
      productName: "Quivio Enclave",
      productSubtitle: "Private AI Knowledge System",
      sectionNavLabel: "Quivio Enclave sections",
      sectionNav: [
        { href: "#trust", label: "Trust" },
        { href: "#workflow", label: "Workflow" },
        { href: "#ops", label: "Ops" },
      ],
      kicker: "On-prem AI knowledge system",
      heroHeading: "Private AI answers for your organization.",
      heroLede:
        "Quivio Enclave KMS uses local AI models to turn internal knowledge into secure, citation-backed answers. Keep sensitive information on premises, off third-party AI platforms, and available to the right teams.",
      ctaPrimary: "Request a walkthrough",
      ctaSecondary: "See the workflow",
      proofPoints: ["Private AI system", "Role-scoped answers", "Audit-ready governance"],
      pillars: [
        {
          eyebrow: "Governed AI ingestion",
          title: "Approve knowledge before AI can use it",
          description:
            "Documents move through explicit submit, approve, reject, archive, and delete workflows before anything is published to retrieval.",
        },
        {
          eyebrow: "Source-grounded RAG",
          title: "Give teams AI answers grounded in approved evidence",
          description:
            "Retrieval is scoped to authorized collections, then answers carry citations, confidence, and retrieval rationale so people can act faster with less guesswork.",
        },
        {
          eyebrow: "Operational governance",
          title: "Operate AI with controls your organization can defend",
          description:
            "Uploads, approvals, queries, downloads, archive access, model activity, and worker lifecycle events are written to durable audit trails for security, compliance, and internal review.",
        },
      ],
      workflow: {
        eyebrow: "The control plane",
        heading: "Turn internal knowledge into controlled AI assistance.",
        body:
          "The system makes document state explicit so organizations can prove what entered the AI retrieval layer, when it became available, and under whose authority.",
        steps: [
          {
            step: "01",
            title: "Submit",
            description:
              "Users upload into permitted collections with metadata and ownership.",
          },
          {
            step: "02",
            title: "Approve",
            description:
              "Admins or approvers review, reject, or release content for ingestion.",
          },
          {
            step: "03",
            title: "Vectorize",
            description:
              "Workers chunk, embed, and publish only approved content to Qdrant.",
          },
          {
            step: "04",
            title: "Answer",
            description:
              "The assistant retrieves authorized evidence and returns citation-backed answers to the right people.",
          },
        ],
      },
      ops: {
        eyebrow: "Deployable by your team",
        heading: "Built for organizations that need AI they can inspect.",
        body:
          "Run it on infrastructure you control and inspect the queues, vectors, logs, and local model layer. Quivio helps organizations adopt AI without sending sensitive knowledge to third-party AI providers.",
        stackNote:
          "Qdrant for vectors. Redis for ingestion work. Postgres for durable state. Seq for logs. Ollama or vLLM for local AI models.",
      },
      finalCta: {
        heading: "Want private AI knowledge access for your organization?",
        body:
          "See how Quivio Enclave KMS can reduce search time, protect sensitive information, keep AI local, and give teams governed answers with citations and audit trails.",
        button: "Start the conversation",
      },
      console: {
        status: "Private AI RAG",
        questionEyebrow: "AI request",
        question: "What changed in the engineering runbook?",
        trace: [
          {
            title: "Policy gate",
            detail: "Role: engineering + kms:read",
          },
          {
            title: "Retrieval",
            detail: "3 approved chunks from kb_engineering",
          },
          {
            title: "Answer",
            detail: "Cited response released with confidence",
          },
        ],
        answerEyebrow: "AI answer",
        answer:
          "Rotation now requires approver sign-off before KMS material leaves enclave storage.",
        auditEyebrow: "Audit trace",
        auditHeading: "Every AI action has a receipt.",
        auditEvents: ["Upload", "Approve", "Query"],
        auditReceipt: "receipt QE-4831 / append-only",
      },
    },
    labels: {
      service: "Service",
      principle: "Principle",
      post: "Post",
    },
    details: {
      info: "Info",
      date: "Date",
      category: "Category",
    },
    thankYou: {
      heading: "Thank You",
      message:
        "Thanks for reaching out! We're doing our happy dance 💃🕺 and will get back to you faster than you can say \"supercalifragilisticexpialidocious\"!",
    },
    footer: {
      rights: "©{{year}} Quivio. All rights reserved.",
      privacy: "Privacy Policy",
      terms: "Terms of Use",
    },
  },
  fr: {
    nav: {
      home: "Accueil",
      services: "Services",
      enclave: "Enclave KMS",
      blog: "Blogue",
      team: "Équipe",
      contact: "Contact",
    },
    misc: {
      skip: "Aller au contenu",
      languageLabel: "Langue",
      languageEnglish: "Anglais",
      languageFrench: "Français",
      themeDark: "Sombre",
      themeLight: "Clair",
      switchToDarkTheme: "Passer au thème sombre",
      switchToLightTheme: "Passer au thème clair",
    },
    banner: {
      eyebrow: "Studio de produits",
      headingPrefix: "Nous sommes",
      headingSuffix: "nous créons des logiciels qui aident les équipes à",
      headingEmphasis: "avancer avec clarté.",
      subtext:
        "Nous concevons et livrons des logiciels fiables pour les organisations qui ont besoin de systèmes propres, d'une pensée produit pragmatique et d'une livraison digne de confiance.",
      cta: "Démarrer un projet",
    },
    homeUpdate: {
      eyebrow: "Mise à jour produit",
      productName: "Quivio Enclave KMS",
      productLabel: "Base de connaissances IA gouvernée",
      heading: "Quivio Enclave KMS est maintenant disponible.",
      body:
        "Une plateforme d'intelligence documentaire gouvernée et sur site pour les équipes qui ont besoin d'IA privée, de réponses selon les rôles, de citations et de pistes d'audit.",
      primaryCta: "Explorer Enclave",
      secondaryCta: "Lire l'annonce",
      panelTitle: "Profil de déploiement",
      panelStatus: "Sur site",
      receiptLabel: "Reçu d'audit",
      points: ["Déploiement sur site", "Recherche RBAC", "Approbations et audit"],
    },
    about: {
      heading: "Conception et ingénierie logicielle pour les opérations complexes",
      bodyLead:
        "Nous aidons les équipes à clarifier le problème, façonner le produit et construire le système qui le soutient,",
      bodyTail:
        "puis à améliorer l'expérience, l'architecture et les résultats d'affaires après le lancement.",
    },
    aiResearch: {
      eyebrow: "Recherche appliquée en IA",
      heading: "De la recherche appliquée en IA pour les organisations qui ont besoin de plus que des démos.",
      body:
        "Nous étudions le flux de travail, les personnes, les données et les contraintes avant de recommander l'IA. Votre équipe obtient ainsi un chemin réaliste vers la valeur, plutôt qu'une série d'expériences isolées.",
      primaryCta: "Voir les services IA",
      secondaryCta: "Parler de préparation IA",
      signalsEyebrow: "Ce que nous validons",
      signalsHeading: "Signaux de préparation",
      signals: ["Points de friction du flux", "Accès au savoir", "Limites de risque et d'approbation", "Responsabilité opérationnelle"],
      tracks: [
        {
          eyebrow: "Recherche de flux",
          title: "Trouver où l'IA peut retirer de la friction",
          description:
            "Nous cartographions les décisions, les transferts et les points d'échec afin d'isoler les occasions IA qui méritent le temps et le budget de votre équipe.",
        },
        {
          eyebrow: "Validation de prototypes",
          title: "Tester le système avant d'élargir la promesse",
          description:
            "Nous transformons les cas d'usage les plus solides en pilotes ciblés afin de mesurer l'utilité, la qualité et l'adoption avant un déploiement plus large.",
        },
        {
          eyebrow: "Conception de gouvernance",
          title: "Définir les contrôles qui rendent l'adoption défendable",
          description:
            "Nous structurons le modèle opérationnel autour de l'accès aux données, de la révision humaine, de l'auditabilité et de la responsabilité pour introduire l'IA sans contourner l'imputabilité.",
        },
      ],
    },
    why: {
      heading: "Comment nous rendons le logiciel plus fiable",
      intro:
        "Un bon logiciel n'est pas seulement du code livré. C'est un langage commun, une architecture solide, une livraison régulière et des preuves claires que le système fait ce qu'il doit faire.",
      items: [
        {
          id: "clientFocused",
          title: "Proches de votre équipe",
          description:
            "Nous travaillons avec des points de contrôle clairs, des boucles de rétroaction rapides et des mises à jour directes afin que le produit reste aligné sur vos vrais objectifs.",
        },
        {
          id: "expertise",
          title: "Jugement d'ingénierie sénior",
          description:
            "Nous transformons les exigences ambiguës en systèmes maintenables, avec des décisions ancrées dans les besoins produit et la réalité technique.",
        },
        {
          id: "efficientProcess",
          title: "Livraison prévisible",
          description:
            "Des sprints ciblés, des décisions validées et des compromis visibles maintiennent l'élan sans cacher la complexité.",
        },
        {
          id: "qualityInnovation",
          title: "Qualité durable",
          description:
            "Nous utilisons les outils modernes avec pragmatisme, en priorisant la fiabilité, la performance et les systèmes que votre équipe peut exploiter dans la durée.",
        },
      ],
    },
    aiFunnel: {
      eyebrow: "Parcours de préparation IA",
      heading: "Un chemin pragmatique entre l'intérêt et une IA opérationnelle.",
      body:
        "Nous aidons les organisations à passer d'expériences dispersées à un modèle opérationnel prêt pour l'IA en validant la valeur par petites boucles, puis en renforçant les processus, les données et les contrôles qui la soutiennent.",
      stagesHeading: "Étapes de préparation IA",
      stageLabel: "Étape",
      resultLabel: "Résultat",
      stages: [
        {
          step: "01",
          eyebrow: "Aligner",
          title: "Bien cadrer le problème d'affaires",
          description:
            "Nous identifions l'objectif, les points de pression du flux, les parties prenantes et les risques afin que tout le monde résolve le même problème.",
          result: "Critères de succès partagés",
        },
        {
          step: "02",
          eyebrow: "Valider",
          title: "Concevoir un pilote appuyé par des preuves",
          description:
            "Nous définissons les intrants, les boucles de révision et les signaux d'évaluation nécessaires pour prouver si l'IA peut aider dans un cadre contrôlé.",
          result: "Portée de pilote crédible",
        },
        {
          step: "03",
          eyebrow: "Opérationnaliser",
          title: "Construire le flux autour du modèle",
          description:
            "Nous relions le système au bon savoir, aux bonnes interfaces et aux bons points de contrôle humains pour qu'il s'insère dans les opérations réelles plutôt que de rester en laboratoire.",
          result: "Flux prêt pour la production",
        },
        {
          step: "04",
          eyebrow: "Gouverner",
          title: "Rendre l'adoption de l'IA durable",
          description:
            "Nous mettons en place la responsabilité, les contrôles et le suivi afin que votre organisation puisse élargir l'usage avec une vision claire de la qualité, du risque et de l'imputabilité.",
          result: "Modèle opérationnel prêt pour l'IA",
        },
      ],
      outcomeEyebrow: "Le résultat",
      outcomeHeading: "À quoi ressemble une organisation prête pour l'IA",
      outcomes: [
        "Une liste priorisée de cas d'usage IA liée aux résultats d'affaires",
        "Des pilotes conçus avec des boucles de révision et des critères de succès mesurables",
        "Des exigences explicites pour le savoir, les données et l'outillage",
        "Des modèles de gouvernance que votre équipe peut exploiter après le lancement",
      ],
    },
    services: {
      heading: "Services",
      subheading: "Les services de Quivio sont pensés pour vos besoins d'affaires",
      items: [
        {
          id: "enterprise",
          title: "Développement d'applications d'entreprise",
          description:
            "Des solutions sur mesure pour optimiser vos opérations. Nous créons des applications robustes et évolutives qui s'intègrent à vos systèmes existants et grandissent avec votre entreprise.",
        },
        {
          id: "uiux",
          title: "Conception UI/UX",
          description:
            "Nous concevons des interfaces centrées sur l'utilisateur qui renforcent votre présence numérique. Nos designs améliorent l'engagement, la productivité et l'expérience globale.",
        },
        {
          id: "cloud",
          title: "Solutions infonuagiques",
          description:
            "Une infrastructure infonuagique sécurisée et flexible adaptée à vos besoins. Nous facilitons la migration vers le cloud pour assurer évolutivité, efficacité des coûts et collaboration accrue.",
        },
        {
          id: "mobile",
          title: "Développement d'applications mobiles",
          description:
            "Des applications mobiles multiplateformes qui captivent les utilisateurs. Nous créons des apps réactives et riches en fonctionnalités pour offrir une expérience fluide sur tous les appareils.",
        },
        {
          id: "appliedAi",
          title: "Recherche appliquée en IA",
          description:
            "Nous aidons les organisations à identifier où l'IA a sa place, à valider le flux avec de vraies preuves et à définir les systèmes et la gouvernance nécessaires pour l'adopter de façon responsable.",
        },
      ],
    },
    contact: {
      heading: "Contact",
      intro:
        "Dites bonjour. Parlez-nous de votre projet et nous répondrons avec des étapes claires, des budgets et des échéanciers. Attendez-vous à des réponses franches, une approche moderne et une livraison fiable.",
      nameLabel: "Nom",
      namePlaceholder: "Nom",
      subjectLabel: "Sujet",
      subjectPlaceholder: "Sujet",
      emailLabel: "Courriel",
      emailPlaceholder: "Courriel",
      messageLabel: "Message",
      messagePlaceholder: "Message",
      submit: "Envoyer le message",
    },
    team: {
      heading: "Notre équipe",
      subheading: "Un travail remarquable, des gens formidables.",
      positions: {
        founder: "Fondateur / Ingénieur logiciel en chef",
        designDirector: "Directrice de la conception",
        softwareEngineer: "Ingénieur logiciel",
      },
    },
    lead: {
      heading: "Confiez-nous la partie difficile",
      subheading: "Nous vous aiderons à la transformer en produit, plateforme ou flux de travail fiable pour votre équipe.",
      cta: "Démarrer la conversation",
    },
    portfolio: {
      heading: "Découvrez notre portfolio",
      intro:
        "Pour chaque problème complexe, il existe une réponse claire, simple et fausse. Nous vous guidons avec des solutions qui embrassent la complexité et apportent de la clarté.",
      viewProject: "Voir le projet",
    },
    blog: {
      heading: "Blogue",
      subheading: "Perspectives, mises à jour et études de cas de l'équipe Quivio.",
      readMore: "Lire la suite",
      backToBlog: "Retour au blogue",
      notFound: "Article introuvable",
      loading: "Chargement de l'article...",
      loadError: "Impossible de charger cet article pour le moment.",
      readAloud: "Lire à voix haute",
      stopReading: "Arrêter la lecture",
      readAloudUnavailable: "La lecture à voix haute n'est pas prise en charge.",
      readingProgress: "Progression de lecture",
    },
    enclave: {
      productAlt: "Quivio Enclave KMS",
      productName: "Quivio Enclave",
      productSubtitle: "Système de connaissances IA privé",
      sectionNavLabel: "Sections Quivio Enclave",
      sectionNav: [
        { href: "#trust", label: "Confiance" },
        { href: "#workflow", label: "Flux" },
        { href: "#ops", label: "Ops" },
      ],
      kicker: "Système de connaissances IA sur site",
      heroHeading: "Des réponses IA privées pour votre organisation.",
      heroLede:
        "Quivio Enclave KMS utilise des modèles d'IA locaux pour transformer le savoir interne en réponses sécurisées et appuyées par des citations. Gardez l'information sensible sur site, hors des plateformes d'IA tierces, et accessible aux bonnes équipes.",
      ctaPrimary: "Demander une présentation",
      ctaSecondary: "Voir le flux",
      proofPoints: ["Système IA privé", "Réponses selon les rôles", "Gouvernance prête pour l'audit"],
      pillars: [
        {
          eyebrow: "Ingestion IA gouvernée",
          title: "Approuver le savoir avant que l'IA puisse l'utiliser",
          description:
            "Les documents passent par des flux explicites de soumission, d'approbation, de rejet, d'archivage et de suppression avant toute publication dans la couche de recherche.",
        },
        {
          eyebrow: "RAG fondé sur les sources",
          title: "Donner aux équipes des réponses IA fondées sur des preuves approuvées",
          description:
            "La recherche est limitée aux collections autorisées, puis les réponses incluent citations, confiance et logique de récupération afin d'accélérer l'action avec moins d'incertitude.",
        },
        {
          eyebrow: "Gouvernance opérationnelle",
          title: "Exploiter l'IA avec des contrôles que votre organisation peut défendre",
          description:
            "Les téléversements, approbations, requêtes, téléchargements, accès aux archives, activité des modèles et événements de travailleurs sont consignés durablement pour la sécurité, la conformité et la revue interne.",
        },
      ],
      workflow: {
        eyebrow: "Le plan de contrôle",
        heading: "Transformez le savoir interne en assistance IA contrôlée.",
        body:
          "Le système rend l'état des documents explicite afin que les organisations puissent prouver ce qui est entré dans la couche de recherche IA, quand cela est devenu disponible et sous quelle autorité.",
        steps: [
          {
            step: "01",
            title: "Soumettre",
            description:
              "Les utilisateurs téléversent dans les collections permises avec métadonnées et propriété.",
          },
          {
            step: "02",
            title: "Approuver",
            description:
              "Les admins ou approbateurs révisent, rejettent ou libèrent le contenu pour l'ingestion.",
          },
          {
            step: "03",
            title: "Vectoriser",
            description:
              "Les travailleurs découpent, intègrent et publient seulement le contenu approuvé dans Qdrant.",
          },
          {
            step: "04",
            title: "Répondre",
            description:
              "L'assistant récupère les preuves autorisées et retourne des réponses avec citations aux bonnes personnes.",
          },
        ],
      },
      ops: {
        eyebrow: "Déployable par votre équipe",
        heading: "Conçu pour les organisations qui ont besoin d'une IA inspectable.",
        body:
          "Exécutez-le sur une infrastructure que vous contrôlez et inspectez les files, les vecteurs, les journaux et la couche de modèles locaux. Quivio aide les organisations à adopter l'IA sans envoyer de connaissances sensibles vers des fournisseurs d'IA tiers.",
        stackNote:
          "Qdrant pour les vecteurs. Redis pour le travail d'ingestion. Postgres pour l'état durable. Seq pour les journaux. Ollama ou vLLM pour les modèles IA locaux.",
      },
      finalCta: {
        heading: "Vous voulez un accès privé au savoir par IA pour votre organisation?",
        body:
          "Découvrez comment Quivio Enclave KMS peut réduire le temps de recherche, protéger l'information sensible, garder l'IA en local et donner aux équipes des réponses IA gouvernées avec citations et pistes d'audit.",
        button: "Démarrer la conversation",
      },
      console: {
        status: "RAG IA privé",
        questionEyebrow: "Demande IA",
        question: "Qu'est-ce qui a changé dans le manuel d'ingénierie?",
        trace: [
          {
            title: "Contrôle d'accès",
            detail: "Rôle : engineering + kms:read",
          },
          {
            title: "Récupération",
            detail: "3 blocs approuvés depuis kb_engineering",
          },
          {
            title: "Réponse",
            detail: "Réponse citée publiée avec confiance",
          },
        ],
        answerEyebrow: "Réponse IA",
        answer:
          "La rotation exige maintenant l'approbation d'un responsable avant que le matériel KMS quitte le stockage de l'enclave.",
        auditEyebrow: "Trace d'audit",
        auditHeading: "Chaque action IA a un reçu.",
        auditEvents: ["Téléverser", "Approuver", "Requête"],
        auditReceipt: "reçu QE-4831 / append-only",
      },
    },
    labels: {
      service: "Service",
      principle: "Principe",
      post: "Article",
    },
    details: {
      info: "Infos",
      date: "Date",
      category: "Catégorie",
    },
    thankYou: {
      heading: "Merci",
      message:
        "Merci de nous avoir contactés ! On fait notre petite danse de joie 💃🕺 et on vous répondra plus vite que vous ne pouvez dire \"supercalifragilisticexpialidocious\" !",
    },
    footer: {
      rights: "©{{year}} Quivio. Tous droits réservés.",
      privacy: "Politique de confidentialité",
      terms: "Conditions d'utilisation",
    },
  },
};

const LanguageContext = createContext({
  language: "en",
  setLanguage: () => {},
  t: () => "",
});

const getValue = (obj, path) => {
  if (!obj || !path) return undefined;
  return path.split(".").reduce((acc, key) => {
    if (acc && Object.prototype.hasOwnProperty.call(acc, key)) {
      return acc[key];
    }
    return undefined;
  }, obj);
};

const interpolate = (value, params) => {
  if (!params || typeof value !== "string") return value;
  return value.replace(/\{\{(\w+)\}\}/g, (match, key) => {
    if (Object.prototype.hasOwnProperty.call(params, key)) {
      return params[key];
    }
    return match;
  });
};

const normalizeLanguage = (language) => (language === "fr" ? "fr" : "en");

const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    if (typeof window === "undefined") return "en";
    const stored = window.localStorage.getItem("quivio-language");
    if (stored) return normalizeLanguage(stored);
    if (navigator.language && navigator.language.toLowerCase().startsWith("fr")) {
      return "fr";
    }
    return "en";
  });

  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.lang = language;
    }
    if (typeof window !== "undefined") {
      window.localStorage.setItem("quivio-language", language);
    }
  }, [language]);

  const t = useCallback((key, params) => {
    const localized = getValue(translations[language], key);
    const fallback = getValue(translations.en, key);
    const value = localized !== undefined ? localized : fallback;
    return interpolate(value !== undefined ? value : key, params);
  }, [language]);

  const value = useMemo(
    () => ({
      language,
      setLanguage,
      t,
    }),
    [language, t]
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

const useTranslation = () => useContext(LanguageContext);

const withTranslation = (Component) => {
  return function WithTranslation(props) {
    const context = useTranslation();
    return <Component {...props} i18n={context} />;
  };
};

export { LanguageProvider, translations, useTranslation, withTranslation };
